import type { GrowthbookFeatures } from "@shared/growthbook";

export const useGrowthbook = <T extends keyof GrowthbookFeatures>({
  enabled = true,
  fallback,
  feature,
}: {
  feature: MaybeRefOrGetter<T>;
  fallback: GrowthbookFeatures[T];
  enabled?: MaybeRefOrGetter<boolean>;
}) => {
  const { $growthbook } = useNuxtApp();

  const loaded = computed(() => {
    if ($growthbook?.errored?.value) return true;

    return Boolean($growthbook?.loaded?.value);
  });

  const getFeatureValue = () => {
    if ($growthbook?.errored?.value) return fallback;

    if (!toValue(enabled)) return fallback;

    try {
      return $growthbook?.features?.value?.getFeatureValue?.<
        GrowthbookFeatures[T]
      >(toValue(feature), fallback);
    } catch {
      return fallback;
    }
  };

  // Create a reactive variable to store and update the feature flag result
  const result = ref(fallback);

  // Optional: Watch the feature flag for changes (requires streaming to be enabled)
  watch(
    [
      loaded,
      () => $growthbook?.features,
      () => toValue(enabled),
      () => toValue(feature),
    ],
    ([newGrowthbookLoaded, newGrowthbookFeature, featureEnabled]) => {
      if (
        !newGrowthbookLoaded ||
        !newGrowthbookFeature?.value ||
        !featureEnabled
      )
        return;

      result.value = getFeatureValue();
    },
    { immediate: true },
  );

  return {
    loaded,
    result,
  };
};

export default useGrowthbook;
